import React, { useState, useMemo } from 'react';
import { User, CheckIn } from '../types';
import { formatTurkeyDate } from '../utils';

interface AdminCheckInHistoryProps {
  users: User[];
  checkIns: CheckIn[];
}

type FilterMode = 'single' | 'range' | 'monthly';

const AdminCheckInHistory: React.FC<AdminCheckInHistoryProps> = ({ users, checkIns }) => {
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [filterMode, setFilterMode] = useState<FilterMode>('single');
  const [singleDate, setSingleDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Create a map of users for quick lookup
  const usersMap = useMemo(() => {
    const map = new Map<string, User>();
    users.forEach(user => map.set(user.id, user));
    return map;
  }, [users]);

  // Generate month options for the last 12 months
  const monthOptions = useMemo(() => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    
    return options;
  }, []);

  // Filter check-ins based on selected criteria
  const filteredCheckIns = useMemo(() => {
    let filtered = [...checkIns];

    // Filter by user
    if (selectedUser !== 'all') {
      filtered = filtered.filter(checkIn => checkIn.userId === selectedUser);
    }

    // Filter by date based on mode
    if (filterMode === 'single' && singleDate) {
      const filterDate = new Date(singleDate);
      filtered = filtered.filter(checkIn => {
        const checkInDate = new Date(checkIn.timestamp);
        return checkInDate.toDateString() === filterDate.toDateString();
      });
    } else if (filterMode === 'range' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date
      
      filtered = filtered.filter(checkIn => {
        const checkInDate = new Date(checkIn.timestamp);
        return checkInDate >= start && checkInDate <= end;
      });
    } else if (filterMode === 'monthly' && selectedMonth) {
      const [year, month] = selectedMonth.split('-').map(Number);
      const startOfMonth = new Date(year, month - 1, 1);
      const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
      
      filtered = filtered.filter(checkIn => {
        const checkInDate = new Date(checkIn.timestamp);
        return checkInDate >= startOfMonth && checkInDate <= endOfMonth;
      });
    }

    // Group check-ins by user and date, keeping only the first check-in of each day per user
    const groupedCheckIns = new Map<string, CheckIn>();
    
    filtered.forEach(checkIn => {
      const checkInDate = new Date(checkIn.timestamp);
      const dateKey = checkInDate.toDateString();
      const userDateKey = `${checkIn.userId}-${dateKey}`;
      
      // Only keep the first check-in of the day for each user
      if (!groupedCheckIns.has(userDateKey)) {
        groupedCheckIns.set(userDateKey, checkIn);
      }
    });

    // Convert back to array and sort by timestamp (newest first)
    return Array.from(groupedCheckIns.values()).sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [checkIns, selectedUser, filterMode, singleDate, startDate, endDate, selectedMonth]);

  // Pagination
  const totalPages = Math.ceil(filteredCheckIns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCheckIns = filteredCheckIns.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSelectedUser('all');
    setFilterMode('single');
    setSingleDate('');
    setStartDate('');
    setEndDate('');
    setSelectedMonth('');
    setCurrentPage(1);
  };

  const getFilterDescription = () => {
    if (filterMode === 'single' && singleDate) {
      return ` - ${new Date(singleDate).toLocaleDateString('tr-TR')}`;
    } else if (filterMode === 'range' && startDate && endDate) {
      return ` - ${new Date(startDate).toLocaleDateString('tr-TR')} - ${new Date(endDate).toLocaleDateString('tr-TR')}`;
    } else if (filterMode === 'monthly' && selectedMonth) {
      const [year, month] = selectedMonth.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      return ` - ${date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}`;
    }
    return '';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Giriş Geçmişi</h2>
        
        {/* Filters */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* User Filter */}
            <div>
              <label htmlFor="user-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Kullanıcı
              </label>
              <select
                id="user-filter"
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Tüm Kullanıcılar</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Mode */}
            <div>
              <label htmlFor="filter-mode" className="block text-sm font-medium text-gray-700 mb-1">
                Filtre Türü
              </label>
              <select
                id="filter-mode"
                value={filterMode}
                onChange={(e) => {
                  setFilterMode(e.target.value as FilterMode);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="single">Tek Gün</option>
                <option value="range">Tarih Aralığı</option>
                <option value="monthly">Aylık</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterMode === 'single' && (
              <div>
                <label htmlFor="single-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Tarih
                </label>
                <input
                  type="date"
                  id="single-date"
                  value={singleDate}
                  onChange={(e) => {
                    setSingleDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}

            {filterMode === 'range' && (
              <>
                <div>
                  <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </>
            )}

            {filterMode === 'monthly' && (
              <div>
                <label htmlFor="month-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Ay Seçin
                </label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Ay seçin</option>
                  {monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {filteredCheckIns.length} giriş bulundu
            {selectedUser !== 'all' && ` - ${usersMap.get(selectedUser)?.name}`}
            {getFilterDescription()}
          </p>
        </div>
      </div>

      {/* Check-ins Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {paginatedCheckIns.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Giriş geçmişi bulunamadı</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanıcı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih & Saat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İş Yeri
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mesafe
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Konum
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCheckIns.map((checkIn) => {
                    const user = usersMap.get(checkIn.userId);
                    return (
                      <tr key={checkIn.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user?.name || 'Bilinmeyen Kullanıcı'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user?.email || 'E-posta yok'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatTurkeyDate(checkIn.timestamp)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user?.workLocation.name || user?.workLocation.address || 'Konum belirtilmemiş'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {checkIn.distance.toFixed(2)} km
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {checkIn.location.address || `${checkIn.location.latitude.toFixed(6)}, ${checkIn.location.longitude.toFixed(6)}`}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Önceki
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sonraki
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{startIndex + 1}</span> -{' '}
                      <span className="font-medium">
                        {Math.min(startIndex + itemsPerPage, filteredCheckIns.length)}
                      </span>{' '}
                      arası, toplam{' '}
                      <span className="font-medium">{filteredCheckIns.length}</span> sonuç
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Önceki
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Sonraki
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCheckInHistory; 